import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Paper,
} from '@mui/material';
import config from './config'; // Assuming you have config.js for backend URL

function InviteCompletion() {
    // Get the token from the URL parameters (:token in the route)
    const { token } = useParams();
    const navigate = useNavigate();

    // State for form fields
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // State for loading and error messages
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [emailFromToken, setEmailFromToken] = useState(''); // To display the email being registered

    // --- Optional: Validate token on component load ---
    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setError("Invalid invitation link: No token provided.");
                return;
            }
            setLoading(true);
            try {
                const response = await fetch(`${config.backendUrl}/invite/${token}`);
                const data = await response.json();

                if (!response.ok || !data.valid) {
                    // Use message from backend if available, otherwise generic
                    throw new Error(data.message || "Invalid or expired invitation link.");
                }
                // Store email to display it (optional)
                setEmailFromToken(data.email);
                 setError(null); // Clear any previous errors

            } catch (err) {
                console.error("Token validation error:", err);
                setError(err.message);
                // Optionally disable the form if token is invalid
            } finally {
                setLoading(false);
            }
        };

        validateToken();
    }, [token]); // Re-run if the token changes (though unlikely for a specific invite)

    // --- Handle Form Submission ---
    const handleSubmit = async (event) => {
        event.preventDefault(); // Prevent default browser form submission
        setError(null); // Clear previous errors
        setSuccessMessage(null);

        // Basic Validation
        if (!firstName || !lastName || !password || !confirmPassword) {
            setError("All fields are required.");
            return;
        }
        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }
        if (!token) {
            setError("Invalid invitation link: No token found.");
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${config.backendUrl}/complete_invitation`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token: token, // Send the token from the URL
                    firstName: firstName,
                    lastName: lastName,
                    password: password,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                // Throw an error with the message from the backend if available
                throw new Error(data.message || `Registration failed: ${response.statusText}`);
            }

            // --- Success ---
            setSuccessMessage("Registration successful! Redirecting...");

            // Automatically log the user in by storing the token returned by /complete_invitation
            if (data.token) {
                localStorage.setItem('token', data.token);
                // Optionally store other user details if needed, e.g., role_id, user_id
                localStorage.setItem('userRole', data.role_id);
                localStorage.setItem('userId', data.user_id);
                localStorage.setItem('team_id', data.team_id); // Store team_id if needed elsewhere
            }

            // Redirect to dashboard after a short delay
            setTimeout(() => {
                navigate('/dashboard'); // Or '/login' if you prefer they log in manually
            }, 1500); // 1.5 second delay

        } catch (err) {
            console.error("Registration error:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container component="main" maxWidth="xs">
            <Paper elevation={3} sx={{ mt: 8, p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography component="h1" variant="h5">
                    Complete Your Registration
                </Typography>
                {emailFromToken && (
                     <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                        Registering account for: {emailFromToken}
                    </Typography>
                )}
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                    {error && <Alert severity="error" sx={{ mb: 2, width: '100%' }}>{error}</Alert>}
                    {successMessage && <Alert severity="success" sx={{ mb: 2, width: '100%' }}>{successMessage}</Alert>}

                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="firstName"
                        label="First Name"
                        name="firstName"
                        autoComplete="given-name"
                        autoFocus
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        disabled={loading || successMessage} // Disable fields on loading/success
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="lastName"
                        label="Last Name"
                        name="lastName"
                        autoComplete="family-name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        disabled={loading || successMessage}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        type="password"
                        id="password"
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading || successMessage}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="confirmPassword"
                        label="Confirm Password"
                        type="password"
                        id="confirmPassword"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        // Add error state if passwords don't match (optional visual feedback)
                        error={password !== confirmPassword && confirmPassword !== ''}
                        helperText={password !== confirmPassword && confirmPassword !== '' ? "Passwords do not match" : ""}
                        disabled={loading || successMessage}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        disabled={loading || successMessage} // Disable button on loading/success
                    >
                        {loading ? <CircularProgress size={24} /> : 'Complete Registration'}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
}

export default InviteCompletion;